import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { IMAGES } from '../mock';

// Use the existing first hero slide image so each option compares apples-to-apples
const HERO_IMG = 'https://images.unsplash.com/photo-1516298252535-cf2ac5147f9b?auto=format&fit=crop&w=2400&q=80';

const HeroFrame = ({ title, subtitle, children }) => (
  <section className="border-b border-white/10 py-12">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-6">
      <span className="font-display tracking-[0.3em] text-[11px] text-[#FF6B00] font-semibold">{title}</span>
      <p className="text-neutral-400 text-sm mt-2 max-w-2xl">{subtitle}</p>
    </div>
    <div className="relative h-[80vh] min-h-[600px] overflow-hidden bg-black">
      {children}
    </div>
  </section>
);

const HeroContent = () => (
  <div className="absolute inset-0 flex items-end pb-20 z-20">
    <div className="max-w-[1400px] w-full mx-auto px-6 lg:px-10 text-white">
      <div className="font-display tracking-[0.3em] text-[11px] text-[#FF6B00] font-semibold mb-4">
        McLAREN &middot; TRACK SPEC
      </div>
      <h1 className="font-display text-5xl md:text-7xl lg:text-[100px] tracking-tight leading-[0.95] max-w-3xl">
        CANADA&rsquo;S LARGEST<br />
        SELECTION OF CARBON.
      </h1>
      <p className="text-neutral-200 mt-5 max-w-xl">
        Premium dry carbon, forged carbon, and bespoke wheels for the world&rsquo;s most iconic chassis.
      </p>
      <Link
        to="/wheels"
        className="mt-7 inline-flex items-center gap-3 bg-[#FF6B00] hover:bg-[#E25500] text-white px-7 py-4 font-display tracking-[0.22em] text-sm font-bold transition-colors"
      >
        SHOP McLAREN <ArrowRight size={16} />
      </Link>
    </div>
  </div>
);

const ScrollHook = () => {
  const [scrolled, setScrolled] = useState(0);
  React.useEffect(() => {
    const onScroll = () => setScrolled(Math.min(window.scrollY, 1200));
    window.addEventListener('scroll', onScroll);
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return scrolled;
};

const HeroOptions = () => {
  const scrolled = ScrollHook();

  return (
    <div className="bg-black text-white pt-32 min-h-screen">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-10">
        <div className="font-display tracking-[0.3em] text-xs text-[#FF6B00] font-semibold mb-3">
          DESIGN PREVIEW
        </div>
        <h1 className="font-display text-5xl md:text-7xl tracking-tight">HERO OPTIONS</h1>
        <p className="text-neutral-400 mt-4 max-w-2xl">
          Three motion treatments for the home page hero. Same image, same copy, different feel.
          Scroll to see Option B&rsquo;s parallax effect react.
        </p>
        <Link to="/" className="inline-block mt-6 text-sm font-display tracking-[0.22em] text-neutral-300 hover:text-white">
          &larr; BACK TO SITE
        </Link>
      </div>

      {/* OPTION A — Slow Ken-Burns zoom (color preserved) */}
      <HeroFrame
        title="OPTION A · KEN-BURNS"
        subtitle="The hero image slowly zooms in over 18 seconds while the orange/red color palette stays vivid. Cinematic but understated."
      >
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{ animation: 'kenBurns 18s ease-in-out infinite alternate' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        <HeroContent />
        <style>{`
          @keyframes kenBurns {
            0%   { transform: scale(1)     translate(0, 0); }
            100% { transform: scale(1.12) translate(-2%, -1%); }
          }
        `}</style>
      </HeroFrame>

      {/* OPTION B — Parallax scroll (color preserved) */}
      <HeroFrame
        title="OPTION B · PARALLAX"
        subtitle="Background image drifts up at half-speed as you scroll. Orange CTA stays vibrant. Most cinematic feel for a single sticky hero."
      >
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            transform: `translateY(${scrolled * 0.4}px) scale(1.05)`,
            transition: 'none',
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/30 to-transparent" />
        <HeroContent />
      </HeroFrame>

      {/* OPTION C — Tinted scan + grain overlay (red/orange duotone hint) */}
      <HeroFrame
        title="OPTION C · CINEMATIC GRADE"
        subtitle="A subtle warm-orange tint, animated scan-line, and grain texture pushes the image into 'movie poster' territory. Most distinctive."
      >
        <img
          src={HERO_IMG}
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          style={{
            filter: 'contrast(1.1) saturate(1.05)',
          }}
        />
        {/* warm orange tint */}
        <div
          className="absolute inset-0 mix-blend-overlay"
          style={{
            background: 'radial-gradient(ellipse at 30% 60%, rgba(255,107,0,0.35) 0%, rgba(225,6,0,0.05) 40%, transparent 70%)',
          }}
        />
        {/* dark bottom + sides */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        {/* moving scan line */}
        <div
          className="absolute left-0 right-0 h-[140px] pointer-events-none"
          style={{
            top: '20%',
            background: 'linear-gradient(180deg, transparent 0%, rgba(255,107,0,0.18) 50%, transparent 100%)',
            animation: 'scan 7s linear infinite',
          }}
        />
        {/* grain noise */}
        <div
          className="absolute inset-0 opacity-25 pointer-events-none mix-blend-overlay"
          style={{
            backgroundImage: 'url("data:image/svg+xml;utf8,<svg xmlns=\\"http://www.w3.org/2000/svg\\" width=\\"160\\" height=\\"160\\" viewBox=\\"0 0 160 160\\"><filter id=\\"n\\"><feTurbulence type=\\"fractalNoise\\" baseFrequency=\\"0.85\\" numOctaves=\\"2\\" stitchTiles=\\"stitch\\"/></filter><rect width=\\"100%\\" height=\\"100%\\" filter=\\"url(%23n)\\" opacity=\\"0.55\\"/></svg>")',
            backgroundSize: '160px 160px',
          }}
        />
        <HeroContent />
        <style>{`
          @keyframes scan {
            0%   { transform: translateY(-200%); }
            100% { transform: translateY(900%);  }
          }
        `}</style>
      </HeroFrame>

      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
        <p className="text-neutral-400 text-sm">
          Tell me which option (A / B / C) and I&rsquo;ll apply it to the live home hero. We can also blend
          ideas &mdash; e.g. Option A&rsquo;s Ken-Burns zoom with Option C&rsquo;s grain overlay.
        </p>
      </div>
    </div>
  );
};

export default HeroOptions;
